import React from 'react';
import { useTranslation } from 'react-i18next';

type TeamMember = {
  id: string;
  name: string;
  email: string;
  role: string;
  avatar: string;
  phone?: string | null;
  assignedProspects: string[];
  stats: {
    totalProspects: number;
    activeProspects: number;
    convertedProspects: number;
    totalMeetings: number;
  };
  joinedDate: string;
  status: string;
};

interface TeamMemberCardProps {
  member: TeamMember;
  roleColor: string;
  onViewDetails: (memberId: string) => void;
}

const TeamMemberCard: React.FC<TeamMemberCardProps> = ({ member, roleColor, onViewDetails }) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'teams' });
  const getRoleColorClasses = (color: string) => {
    const colorMap: { [key: string]: { badge: string; avatar: string } } = {
      amber: {
        badge: 'bg-amber-100 text-amber-700 border-amber-300',
        avatar: 'from-amber-500 to-amber-700'
      },
      indigo: {
        badge: 'bg-indigo-100 text-indigo-700 border-indigo-300',
        avatar: 'from-indigo-500 to-indigo-700'
      },
      purple: {
        badge: 'bg-purple-100 text-purple-700 border-purple-300',
        avatar: 'from-purple-500 to-purple-700'
      },
      blue: {
        badge: 'bg-blue-100 text-blue-700 border-blue-300',
        avatar: 'from-blue-500 to-blue-700'
      },
      green: {
        badge: 'bg-green-100 text-green-700 border-green-300',
        avatar: 'from-green-500 to-green-700'
      },
      orange: {
        badge: 'bg-orange-100 text-orange-700 border-orange-300',
        avatar: 'from-orange-500 to-orange-700'
      },
      pink: {
        badge: 'bg-pink-100 text-pink-700 border-pink-300',
        avatar: 'from-pink-500 to-pink-700'
      },
    };
    return colorMap[color] || {
      badge: 'bg-gray-100 text-gray-700 border-gray-300',
      avatar: 'from-gray-500 to-gray-700'
    };
  };

  const colors = getRoleColorClasses(roleColor);

  return (
    <div
      onClick={() => onViewDetails(member.id)}
      className="group bg-white rounded-xl shadow-sm border-2 border-gray-200 hover:shadow-xl hover:border-[#4339A0] transition-all cursor-pointer"
    >
      <div className="p-5">
        <div className="flex flex-col items-center text-center mb-5">
          <div className={`w-16 h-16 bg-gradient-to-br ${colors.avatar} rounded-2xl flex items-center justify-center text-white font-bold text-xl shadow-lg mb-3`}>
            {member.avatar}
          </div>
          <h3 className="text-base font-bold text-gray-900 mb-2">{member.name}</h3>
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${colors.badge}`}>
            {member.role}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-5">
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-gray-900 mb-1">{member.stats.totalProspects}</p>
            <p className="text-xs text-gray-500 font-medium">{t('stats.totalProspects')}</p>
          </div>

          <div className="bg-emerald-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-emerald-700 mb-1">{member.stats.convertedProspects}</p>
            <p className="text-xs text-emerald-700 font-medium">{t('stats.converted')}</p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default TeamMemberCard;
